import { useId, useState } from "react";
import { Box, IconButton, Link, Paper, Stack, Typography } from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Link as RouterLink } from "react-router-dom";
import { Info } from "@/components/VulnerabilityWatch/Info.tsx";
import type { KnownPortalOverview, PortalGroup, PortalItem } from "@/types";
import { groupHref, itemHref, sharePercent, yourProductAddresses } from "@/utils/portalOverview.ts";
import { sourcesHref } from "@/utils/sourcesFilters.ts";
import * as copy from "@/utils/vulnerabilityCopy.ts";

const ROW_COLUMNS = { xs: "minmax(0, 1fr) auto auto", md: "minmax(0, 2fr) minmax(0, 3fr) 9rem 9rem auto" } as const;

function TopItems({ items }: { readonly items: readonly PortalItem[] }) {
  return (
    <Box component="ol" sx={{ m: 0, mt: 1, pl: 3 }}>
      {items.map((item) => (
        <li key={`${item.kind}:${item.key}`}>
          <Link component={RouterLink} to={itemHref(item)} sx={{ fontFamily: item.kind === "cve" ? "monospace" : undefined }}>
            {copy.itemLabel(item)}
          </Link>
          <Typography component="span" variant="body2" color="text.secondary">
            {` · ${copy.addressesLabel(item.addresses)} · ${copy.requestsLabel(item.requests)}`}
          </Typography>
        </li>
      ))}
    </Box>
  );
}

function YourProduct({ addresses }: { readonly addresses: number | null }) {
  if (addresses === null) {
    return <Typography variant="caption" color="text.secondary">{copy.inYourProductLabel(null)}</Typography>;
  }
  return (
    <Link component={RouterLink} to={sourcesHref({ yourProduct: true })} variant="caption">
      {copy.inYourProductLabel(addresses)}
    </Link>
  );
}

function LookedForRow({ group, most, yourProduct }: {
  readonly group: PortalGroup;
  readonly most: number;
  readonly yourProduct: number | null | undefined;
}) {
  const [open, setOpen] = useState(false);
  const labelId = useId();
  return (
    <Box>
      <Box sx={{ display: "grid", gridTemplateColumns: ROW_COLUMNS, alignItems: "center", columnGap: 2 }}>
        <Link id={labelId} component={RouterLink} to={groupHref(group.group)}>{copy.groupLabel(group.group)}</Link>
        <Box aria-hidden sx={{ display: { xs: "none", md: "block" }, height: 8, borderRadius: 1, bgcolor: "action.hover" }}>
          <Box sx={{ width: `${sharePercent(group.addresses, most)}%`, height: "100%", borderRadius: 1, bgcolor: "primary.main" }} />
        </Box>
        <Typography variant="body2">{copy.addressesLabel(group.addresses)}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ display: { xs: "none", md: "block" } }}>
          {copy.requestsLabel(group.requests)}
        </Typography>
        <IconButton size="small" aria-expanded={open} aria-label={open ? copy.HIDE_TOP : copy.SHOW_TOP}
          aria-describedby={labelId} disabled={group.top_items.length === 0} onClick={() => setOpen((value) => !value)}>
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>
      {yourProduct !== undefined && <YourProduct addresses={yourProduct} />}
      {open && <TopItems items={group.top_items} />}
    </Box>
  );
}

export default function LookedForSection({ overview }: { readonly overview: KnownPortalOverview | null }) {
  const groups = overview?.groups ?? null;
  const most = Math.max(1, ...(groups ?? []).map((group) => group.addresses));
  return (
    <Paper variant="outlined" component="section" aria-label={copy.LOOKED_FOR} sx={{ p: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{copy.LOOKED_FOR}<Info text={copy.TIP_LOOKED_FOR} /></Typography>
      {groups === null && <Typography color="text.secondary">{copy.NOT_KNOWN}</Typography>}
      <Stack spacing={1.5}>
        {(groups ?? []).map((group) => (
          <LookedForRow key={group.group} group={group} most={most} yourProduct={yourProductAddresses(overview, group)} />
        ))}
      </Stack>
    </Paper>
  );
}
